import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import RequireAuth from '../auth/RequireAuth'
import { useAuth } from '../auth/AuthProvider'
import {
  api,
  ApiError,
  type UserSettingsPayload,
  type UserSettingsResponse,
  type LanguageModeOption,
  type KnowledgeScopeOption,
  type ToneLevelOption,
} from '../api'
import {
  LANGUAGE_MODE_OPTIONS,
  KNOWLEDGE_SCOPE_OPTIONS,
  TONE_LEVEL_OPTIONS,
  getLanguageModeLabel,
  getKnowledgeScopeLabel,
  getToneLevelLabel,
  normalizeLanguageMode,
  type PreferenceOption,
} from '../constants/chatPreferences'

type SettingsValues = {
  language_mode: LanguageModeOption
  knowledge_scope: KnowledgeScopeOption
  tone_level: ToneLevelOption
}

const FALLBACK_SETTINGS: SettingsValues = {
  language_mode: 'modern',
  knowledge_scope: 'bounded',
  tone_level: 'adult',
}

const TRUSTED_RETURN_ORIGINS = [] as const

type FieldTooltipMap = Partial<Record<keyof SettingsValues, string>>

type MetaState = {
  canEditTone: boolean
  isGuest: boolean
}

const FIELD_TOOLTIPS: FieldTooltipMap = {
  knowledge_scope:
    'Full context allows the figure to reference modern knowledge. Historical-only limits answers to what they knew during their lifetime.',
  tone_level: 'Some education accounts restrict tone changes for compliance reasons.',
}

const mergeSettings = (response: UserSettingsResponse | null): { values: SettingsValues; defaults: SettingsValues } => {
  const defaults = {
    ...FALLBACK_SETTINGS,
    ...(response?.defaults ?? {}),
    language_mode: normalizeLanguageMode((response?.defaults ?? {}).language_mode ?? FALLBACK_SETTINGS.language_mode),
  } as SettingsValues

  return {
    defaults,
    values: {
      language_mode: normalizeLanguageMode(response?.language_mode ?? defaults.language_mode),
      knowledge_scope: response?.knowledge_scope ?? defaults.knowledge_scope,
      tone_level: response?.tone_level ?? defaults.tone_level,
    },
  }
}

const SettingsField = <T extends string>({
  id,
  label,
  options,
  value,
  defaultLabel,
  onChange,
  disabled,
  tooltip,
  usingDefault,
}: {
  id: string
  label: string
  options: PreferenceOption<T>[]
  value: T
  defaultLabel: string
  onChange: (next: T) => void
  disabled?: boolean
  tooltip?: string
  usingDefault?: boolean
}) => {
  const selectedOption = options.find((option) => option.value === value)

  return (
    <div className={`settings-field${disabled ? ' is-disabled' : ''}`}>
      <div className="settings-field-heading">
        <label htmlFor={id}>{label}</label>
        <div className="settings-field-meta">
          <span className="settings-default">Default: {defaultLabel}</span>
          {usingDefault && <span className="settings-pill">Using default</span>}
          {tooltip && (
            <span className="settings-tooltip" role="img" aria-label={`${label} help`} title={tooltip}>
              i
            </span>
          )}
        </div>
      </div>
      <select id={id} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value as T)}>
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
      {selectedOption?.description && <p className="settings-field-help">{selectedOption.description}</p>}
    </div>
  )
}

const SettingsHeader = () => (
  <header className="settings-header">
    <p className="eyebrow">Account controls</p>
    <h1>Conversation settings</h1>
    <p>
      Tune how historical figures speak to you. These are your account defaults. You can override them per thread inside
      a chat.
    </p>
  </header>
)

const SettingsInfoAside = () => (
  <aside className="settings-card settings-card--secondary">
    <h3>How these settings work</h3>
    <ul>
      <li>Changes sync to the chat service instantly after saving.</li>
      <li>Defaults apply whenever you reset or remove a preference.</li>
      <li>Admins can override tone controls for compliance reasons.</li>
    </ul>
  </aside>
)

const SettingsSignInPrompt = () => (
  <section className="settings-page">
    <SettingsHeader />
    <div className="settings-grid">
      <section className="settings-card">
        <h2>Personalize responses</h2>
        <div className="settings-empty">
          <p>You need to be signed in to edit your assistant preferences.</p>
          <p className="settings-field-help">Use the login button below, then return to this page.</p>
          <Link className="button" to="/login">
            Go to login
          </Link>
        </div>
      </section>
      <SettingsInfoAside />
    </div>
  </section>
)

const SettingsView = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { token, refresh, logout } = useAuth()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [serverValues, setServerValues] = useState<UserSettingsResponse | null>(null)
  const [formValues, setFormValues] = useState<SettingsValues>(FALLBACK_SETTINGS)
  const [initialValues, setInitialValues] = useState<SettingsValues>(FALLBACK_SETTINGS)
  const [defaults, setDefaults] = useState<SettingsValues>(FALLBACK_SETTINGS)
  const [meta, setMeta] = useState<MetaState>({ canEditTone: true, isGuest: false })
  const [canReturn, setCanReturn] = useState(false)

  const returnParam = useMemo(() => {
    if (!location.search) {
      return null
    }
    const params = new URLSearchParams(location.search)
    const value = params.get('return')?.trim()
    return value ? value : null
  }, [location.search])

  const trustedReturnUrl = useMemo(() => {
    if (!returnParam || typeof window === 'undefined') {
      return null
    }

    try {
      const parsed = new URL(returnParam, window.location.origin)
      const allowedOrigins = [window.location.origin, ...TRUSTED_RETURN_ORIGINS]
      return allowedOrigins.includes(parsed.origin) ? parsed.toString() : null
    } catch {
      return null
    }
  }, [returnParam])

  useEffect(() => {
    if (!trustedReturnUrl) {
      setCanReturn(false)
    }
  }, [trustedReturnUrl])

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  useEffect(() => {
    if (!successMessage || typeof window === 'undefined') {
      return
    }
    const timer = window.setTimeout(() => setSuccessMessage(null), 4500)
    return () => window.clearTimeout(timer)
  }, [successMessage])

  useEffect(() => {
    if (!token) {
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    api
      .getUserSettings(token)
      .then((response) => {
        if (cancelled) {
          return
        }
        const { defaults: mergedDefaults, values } = mergeSettings(response)
        setDefaults(mergedDefaults)
        setFormValues(values)
        setInitialValues(values)
        setServerValues(response)
        setMeta({
          canEditTone: response?.can_edit_tone ?? true,
          isGuest: Boolean(response?.is_guest),
        })
      })
      .catch((err) => {
        if (cancelled) {
          return
        }
        if (err instanceof ApiError && err.status === 401) {
          setError('Your session expired. Log in again to edit these settings.')
          refresh()
        } else {
          const message = err instanceof ApiError ? err.message : 'Unable to load your settings right now.'
          setError(message)
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [token, refresh])

  const hasChanges = useMemo(() => {
    return (
      formValues.language_mode !== initialValues.language_mode ||
      formValues.knowledge_scope !== initialValues.knowledge_scope ||
      formValues.tone_level !== initialValues.tone_level
    )
  }, [formValues, initialValues])

  const fieldsDisabled = !token
  const toneDisabled = fieldsDisabled || meta.isGuest || !meta.canEditTone

  const handleSelectChange = useCallback(
    <T extends keyof SettingsValues>(key: T, value: SettingsValues[T]) => {
      setFormValues((prev) => ({ ...prev, [key]: value }))
      setCanReturn(false)
    },
    [setCanReturn]
  )

  const handleSave = useCallback(async () => {
    if (!token || !hasChanges) {
      return
    }

    const payload: UserSettingsPayload = {}
    const changedKeys = ['language_mode', 'knowledge_scope', 'tone_level'] as const
    changedKeys.forEach((key) => {
      if (formValues[key] !== initialValues[key]) {
        payload[key] = formValues[key]
      }
    })

    setSaving(true)
    setError(null)
    setSuccessMessage(null)

    try {
      const updated = await api.updateUserSettings(payload, token)
      const { defaults: mergedDefaults, values } = mergeSettings(updated)
      setDefaults(mergedDefaults)
      setFormValues(values)
      setInitialValues(values)
      setServerValues(updated)
      setMeta({
        canEditTone: updated?.can_edit_tone ?? true,
        isGuest: Boolean(updated?.is_guest),
      })
      setCanReturn(Boolean(trustedReturnUrl))
      setSuccessMessage('Settings saved successfully.')
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError('Your session expired before we could save. Log in and try again.')
        refresh()
      } else {
        const message = err instanceof ApiError ? err.message : 'Unable to save your settings right now.'
        setError(message)
      }
    } finally {
      setSaving(false)
    }
  }, [formValues, initialValues, token, hasChanges, refresh, trustedReturnUrl])

  const renderContent = () => {
    if (!token) {
      return (
        <div className="settings-empty">
          <p>Your authentication token is missing. Refresh the page and sign in again.</p>
        </div>
      )
    }

    if (loading) {
      return <p>Loading your current settings...</p>
    }

    return (
      <>
        <SettingsField
          id="language-mode"
          label="Language style"
          options={LANGUAGE_MODE_OPTIONS}
          value={formValues.language_mode}
          defaultLabel={getLanguageModeLabel(defaults.language_mode)}
          usingDefault={!serverValues?.language_mode}
          tooltip="Choose how formal the figure's speech should be."
          disabled={fieldsDisabled}
          onChange={(value) => handleSelectChange('language_mode', value)}
        />
        <SettingsField
          id="knowledge-scope"
          label="Knowledge scope"
          options={KNOWLEDGE_SCOPE_OPTIONS}
          value={formValues.knowledge_scope}
          defaultLabel={getKnowledgeScopeLabel(defaults.knowledge_scope)}
          usingDefault={!serverValues?.knowledge_scope}
          tooltip={FIELD_TOOLTIPS.knowledge_scope}
          disabled={fieldsDisabled}
          onChange={(value) => handleSelectChange('knowledge_scope', value)}
        />
        <SettingsField
          id="tone-level"
          label="Tone"
          options={TONE_LEVEL_OPTIONS}
          value={formValues.tone_level}
          defaultLabel={getToneLevelLabel(defaults.tone_level)}
          usingDefault={!serverValues?.tone_level}
          tooltip={FIELD_TOOLTIPS.tone_level}
          disabled={toneDisabled}
          onChange={(value) => handleSelectChange('tone_level', value)}
        />
        {meta.isGuest && (
          <p className="settings-field-help">You're currently in guest mode, so tone controls are locked.</p>
        )}
        {!meta.isGuest && !meta.canEditTone && (
          <p className="settings-field-help">Your organization administrator disabled tone changes for this account.</p>
        )}
      </>
    )
  }

  return (
    <section className="settings-page">
      <div className="page-actions">
        <button type="button" className="button ghost" onClick={() => navigate('/dashboard')}>
          Back to Dashboard
        </button>
        <button type="button" className="button" onClick={handleLogout}>
          Log out
        </button>
      </div>
      <SettingsHeader />

      {error && <div className="settings-status settings-status--error">{error}</div>}
      {successMessage && <div className="settings-status settings-status--success">{successMessage}</div>}

      <div className="settings-grid">
        <section className="settings-card">
          <h2>Personalize responses</h2>
          {renderContent()}
          <div className="settings-actions">
            <button type="button" className="settings-save" disabled={!token || !hasChanges || saving} onClick={handleSave}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            {!hasChanges && token && !fieldsDisabled && <span className="settings-field-help">No unsaved changes.</span>}
          </div>

          {canReturn && trustedReturnUrl && (
            <div className="settings-return-cta">
              <p>All set? Head back to the legacy dashboard.</p>
              <a className="button ghost" href={trustedReturnUrl}>
                Return to dashboard
              </a>
            </div>
          )}
        </section>

        <SettingsInfoAside />
      </div>
    </section>
  )
}

const UserSettings = () => (
  <RequireAuth fallback={<SettingsSignInPrompt />}>
    <SettingsView />
  </RequireAuth>
)

export default UserSettings
